import { RefundAllocationPreview, useRefundPreview } from "./RefundAllocationPreview";
import { RefundDocumentPanel } from "./RefundDocumentPanel";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  Field,
  Inline,
  Spinner,
  Stack,
  StatusBadge,
  TextAreaField
} from "@leylion/design-system";
import {
  createIdempotencyKey,
  requestCashierRefund,
  getCashierRefunds,
  getOrderWorkflow,
  searchCashierTransactions
} from "../api/client";
import type {
  OrderWorkflow,
  Receipt,
  Refund,
  TransactionSummary
} from "../api/cashier-types";
import { formatMoneyMinor } from "./cashier-money";
import { GiftCardReplacementFields, giftReplacementInput } from "./GiftCardReplacementFields";
import { RefundGiftCardPrintPanel } from "./RefundGiftCardPrintPanel";
import { valueError } from "../shared/value-copy";

type Language = "ar" | "en";

type Props = {
  language: Language;
  open: boolean;
  onClose: () => void;
  onPrintReceipt: (receipt: Receipt, mode: "payment" | "fiscal") => void;
  onCorrectCustomer?: (workflow: OrderWorkflow) => void;
  requiresManagerRefundApproval: boolean;
  canSetGiftCardExpiry?: boolean;
  onOpenManagerApprovals?: () => void;
};

type PendingRefund = {
  args: Parameters<typeof requestCashierRefund>;
  replacementRequired: boolean;
  noExpiry: boolean;
  expiry: string;
};

const closedStatuses = new Set(["Completed", "Cancelled", "Refunded"]);

function money(minor: number, currency: string, language: Language): string {
  return formatMoneyMinor(minor, currency, language === "ar" ? "ar-AE" : "en-AE");
}

function dateTime(value: string | null, language: Language): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString(language === "ar" ? "ar-AE" : "en-AE", {
        dateStyle: "medium",
        timeStyle: "short"
      });
}

function statusLabel(status: TransactionSummary["orderStatus"], language: Language): string {
  const labels: Record<TransactionSummary["orderStatus"], { ar: string; en: string }> = {
    Draft: { ar: "مسودة", en: "Draft" },
    Held: { ar: "معلّق", en: "Held" },
    SentToKitchen: { ar: "قيد التحضير", en: "Preparing" },
    Ready: { ar: "جاهز", en: "Ready" },
    Completed: { ar: "مكتمل", en: "Completed" },
    Cancelled: { ar: "ملغي", en: "Cancelled" },
    Refunded: { ar: "مسترجع", en: "Refunded" }
  };
  return labels[status][language];
}

function paymentLabel(status: TransactionSummary["paymentStatus"], language: Language): string {
  const labels: Record<TransactionSummary["paymentStatus"], { ar: string; en: string }> = {
    Unpaid: { ar: "غير مدفوع", en: "Unpaid" },
    PartiallyPaid: { ar: "مدفوع جزئياً", en: "Partially paid" },
    Paid: { ar: "مدفوع", en: "Paid" },
    PartiallyRefunded: { ar: "مسترجع جزئياً", en: "Partially refunded" },
    Refunded: { ar: "مسترجع", en: "Refunded" }
  };
  return labels[status][language];
}

function serviceLabel(serviceType: TransactionSummary["serviceType"], language: Language): string {
  const labels: Record<TransactionSummary["serviceType"], { ar: string; en: string }> = {
    WalkIn: { ar: "استلام", en: "Pickup" },
    DineIn: { ar: "داخلي", en: "Dine-in" },
    Takeaway: { ar: "سفري", en: "Takeaway" },
    Delivery: { ar: "توصيل", en: "Delivery" }
  };
  return labels[serviceType][language];
}

function orderTone(status: TransactionSummary["orderStatus"]): "neutral" | "success" | "warning" {
  if (status === "Completed") return "success";
  if (status === "Cancelled" || status === "Refunded") return "warning";
  return "neutral";
}

function paymentTone(status: TransactionSummary["paymentStatus"]): "neutral" | "info" | "success" | "warning" {
  if (status === "Paid") return "success";
  if (status === "PartiallyPaid" || status === "PartiallyRefunded") return "warning";
  if (status === "Refunded") return "info";
  return "neutral";
}

function canPrintFiscal(receipt: Receipt | null): boolean {
  return Boolean(
    receipt
      && receipt.receiptType === "Sale"
      && (receipt.fiscalDocumentType === "StandardTaxInvoice"
        || receipt.fiscalDocumentType === "SimplifiedTaxInvoice")
  );
}

export function CashierClosedOrdersDrawer({
  language,
  open,
  onClose,
  onPrintReceipt,
  onCorrectCustomer,
  canSetGiftCardExpiry = false,
  onOpenManagerApprovals
}: Props) {
  const ar = language === "ar";
  const [query, setQuery] = useState("");
  const [transactions, setTransactions] = useState<TransactionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<OrderWorkflow | null>(null);
  const [selectedRefunds, setSelectedRefunds] = useState<Refund[]>([]);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [refundMode, setRefundMode] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundQuantities, setRefundQuantities] = useState<Record<string, number>>({});
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundApprovalSubmitted, setRefundApprovalSubmitted] = useState(false);
  const [replacementRequired, setReplacementRequired] = useState(false);
  const [noExpiry, setNoExpiry] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [pendingRefund, setPendingRefund] = useState<PendingRefund | null>(null);
  // Closing the drawer must not turn an uncertain response into a new command.
  // Keep each frozen request with its order until the server gives a definite result.
  const pendingByOrder = useRef(new Map<string, PendingRefund>());
  const refundBusyRef = useRef(false);
  const viewGeneration = useRef(0);
  const listGeneration = useRef(0);

  const resetRefundForm = () => {
    setRefundMode(false);
    setRefundReason("");
    setRefundQuantities({});
    setReplacementRequired(false);
    setNoExpiry(false);
    setExpiry("");
    setPendingRefund(null);
  };

  const closeDrawer = () => {
    viewGeneration.current++;
    listGeneration.current++;
    onClose();
  };

  const loadTransactions = async (search = query, clearError = true) => {
    const generation = ++listGeneration.current;
    setLoading(true);
    if (clearError) setError("");
    try {
      const items = await searchCashierTransactions(search.trim() || undefined);
      if (generation !== listGeneration.current) return;
      setTransactions(items.filter((item) => closedStatuses.has(item.orderStatus)));
    } catch (caught) {
      if (generation === listGeneration.current)
        setError(caught instanceof Error ? caught.message : "Failed to load closed orders.");
    } finally {
      if (generation === listGeneration.current) setLoading(false);
    }
  };

  useEffect(() => {
    viewGeneration.current++;
    if (!open) return;
    setSelected(null);
    setSelectedRefunds([]);
    setRefundApprovalSubmitted(false);
    setSelectedLoading(false);
    resetRefundForm();
    void loadTransactions("");
    return () => { viewGeneration.current++; listGeneration.current++; };
    // The drawer refreshes once on every explicit open. Search is user-driven afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sortedTransactions = useMemo(
    () => [...transactions].sort((a, b) => Date.parse(b.completedAt ?? b.createdAt) - Date.parse(a.completedAt ?? a.createdAt)),
    [transactions]
  );

  const refundedByLine = useMemo(() => {
    const result = new Map<string, number>();
    selectedRefunds
      .filter((refund) => refund.status === "Completed")
      .forEach((refund) => {
        refund.lines.forEach((line) => {
          result.set(
            line.originalOrderLineId,
            (result.get(line.originalOrderLineId) ?? 0) + line.quantityMilli
          );
        });
      });
    return result;
  }, [selectedRefunds]);

  const refundableLines = useMemo(() => {
    if (!selected) return [];
    const source = selected.receipt?.lines.map((line) => ({
      id: line.orderLineId,
      name: ar ? line.productNameAr : line.productNameEn,
      quantityMilli: line.quantityMilli
    })) ?? selected.cart.lines.map((line) => ({
      id: line.id,
      name: ar ? line.productNameAr : line.productNameEn,
      quantityMilli: line.quantityMilli
    }));

    return source
      .map((line) => ({
        ...line,
        availableQuantityMilli: Math.max(
          0,
          line.quantityMilli - (refundedByLine.get(line.id) ?? 0)
        )
      }))
      .filter((line) => line.availableQuantityMilli > 0);
  }, [selected, refundedByLine, ar]);

  const completedRefundCount = selectedRefunds.filter((refund) => refund.status === "Completed").length;

  const openTransaction = async (transaction: TransactionSummary) => {
    const generation = ++viewGeneration.current;
    setSelectedLoading(true);
    setError("");
    setRefundApprovalSubmitted(false);
    resetRefundForm();
    try {
      const [workflow, refunds] = await Promise.all([
        getOrderWorkflow(transaction.orderId),
        getCashierRefunds(transaction.orderId)
      ]);
      if (generation !== viewGeneration.current) return;
      setSelected(workflow);
      setSelectedRefunds(refunds);
      const pending = pendingByOrder.current.get(transaction.orderId);
      if (pending) {
        setPendingRefund(pending);
        setRefundMode(true);
        setRefundReason(pending.args[1]);
        setRefundQuantities(Object.fromEntries(pending.args[2].map(line => [line.originalOrderLineId, line.quantityMilli])));
        setReplacementRequired(pending.replacementRequired);
        setNoExpiry(pending.noExpiry);
        setExpiry(pending.expiry);
      }
    } catch (caught) {
      if (generation === viewGeneration.current)
        setError(caught instanceof Error ? caught.message : "Failed to load transaction details.");
    } finally {
      if (generation === viewGeneration.current) setSelectedLoading(false);
    }
  };

  const refundLines = refundableLines
      .map((line) => ({
        originalOrderLineId: line.id,
        quantityMilli: Math.min(
          line.availableQuantityMilli,
          refundQuantities[line.id] ?? 0
        )
      }))
      .filter((line) => line.quantityMilli > 0);
  const allocationPreview = useRefundPreview(selected?.order.id, selected?.order.version, refundLines, open && refundMode && !pendingRefund);

  const processRefund = async () => {
    if (!selected || refundBusyRef.current) return;
    const lines = refundLines;
    if (!pendingRefund && (!lines.length || !refundReason.trim() || !allocationPreview.preview)) return;

    const orderId = selected.order.id;
    const generation = viewGeneration.current;
    refundBusyRef.current = true;
    setRefundBusy(true);
    setError("");
    try {
      const pending = pendingRefund ?? {
        args: [selected, refundReason.trim(), lines, createIdempotencyKey(),
          replacementRequired ? giftReplacementInput(noExpiry, expiry, language) : undefined, allocationPreview.preview!.previewToken] as Parameters<typeof requestCashierRefund>,
        replacementRequired, noExpiry, expiry
      };
      pendingByOrder.current.set(orderId, pending);
      setPendingRefund(pending);
      const result = await requestCashierRefund(...pending.args);
      if (!result.approvalRequired && (!result.executed || !result.refund))
        throw new Error("Refund orchestration returned no terminal result.");
      pendingByOrder.current.delete(orderId);
      if (generation !== viewGeneration.current) return;
      resetRefundForm();
      if (result.approvalRequired) {
        setRefundApprovalSubmitted(true);
        return;
      }
      // The command already committed. Preserve its result and print access even
      // if the following read fails; never present that read failure as a retryable refund.
      const completed = result.refund!;
      setSelectedRefunds(current => [completed, ...current.filter(item => item.id !== completed.id)]);
      try {
        const [workflow, refunds] = await Promise.all([
          getOrderWorkflow(orderId), getCashierRefunds(orderId)
        ]);
        if (generation !== viewGeneration.current) return;
        setSelected(workflow);
        setSelectedRefunds(refunds);
      } catch {
        if (generation !== viewGeneration.current) return;
        setError(ar ? "الاسترداد محفوظ. تعذر تحديث المعاملة؛ أعد فتحها لتحديث البيانات." : "The refund is saved. Reopen the transaction to refresh its details.");
      }
      void loadTransactions(query, false);
    } catch (caught) {
      const failure = caught as { status?: number; problem?: { title?: string } };
      const code = failure.problem?.title;
      const definiteRejection = [400, 401, 403, 404, 409, 422].includes(failure.status ?? 0)
        && code !== "SESSION_CONTEXT_STALE_RESPONSE" && code !== "SESSION_CONTEXT_CHANGED";
      if (definiteRejection) pendingByOrder.current.delete(orderId);
      if (generation !== viewGeneration.current) return;
      if (definiteRejection) setPendingRefund(null);
      if (code === "REFUND_PREVIEW_CHANGED") { allocationPreview.refresh(); setError(ar ? "تغير الطلب أو توزيع الاسترداد. راجع المعاينة المحدّثة قبل التأكيد." : "The order or refund allocation changed. Review the refreshed preview before confirming."); return; }
      if (code === "GIFT_CARD_REPLACEMENT_REQUIRED") {
        setReplacementRequired(true);
        return;
      }
      setError(valueError(caught, language));
    } finally {
      refundBusyRef.current = false;
      setRefundBusy(false);
    }
  };

  return (
    <Drawer
      className="cashier-closed-orders-drawer"
      open={open}
      onClose={closeDrawer}
      title={ar ? "الطلبات المغلقة" : "Closed orders"}
    >
      <div className="cashier-closed-orders" data-testid="closed-orders-drawer">
        <form
          className="cashier-closed-orders__search"
          onSubmit={(event) => {
            event.preventDefault();
            void loadTransactions();
          }}
        >
          <Field
            data-testid="closed-orders-search"
            label={ar ? "بحث برقم الطلب أو العميل أو الإيصال" : "Search order, customer or receipt"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button type="submit" size="sm" disabled={loading}>
            {ar ? "بحث" : "Search"}
          </Button>
        </form>

        {error ? (
          <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Unable to complete operation"} onDismiss={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <div className="cashier-closed-orders__meta">
          <strong>{ar ? "المعاملات المغلقة" : "Closed transactions"}</strong>
          <span>{sortedTransactions.length}</span>
        </div>

        {selectedLoading ? <Spinner label={ar ? "جارٍ تحميل المعاملة" : "Loading transaction"} /> : null}

        {selected ? (
          <section className="cashier-closed-orders__detail" data-testid="closed-order-detail">
            <div className="cashier-closed-orders__detail-head">
              <div>
                <small>{ar ? "المعاملة المحددة" : "Selected transaction"}</small>
                <strong>#{selected.order.orderNumber}</strong>
              </div>
              <Inline>
                <StatusBadge tone={selected.order.status === "Completed" ? "success" : "warning"}>
                  {statusLabel(selected.order.status, language)}
                </StatusBadge>
                <StatusBadge tone={selected.order.paymentStatus === "Paid" ? "success" : "warning"}>
                  {paymentLabel(selected.order.paymentStatus, language)}
                </StatusBadge>
              </Inline>
            </div>

            <div className="cashier-closed-orders__detail-grid">
              <span><small>{ar ? "الخدمة" : "Service"}</small><strong>{serviceLabel(selected.order.serviceType, language)}</strong></span>
              <span><small>{ar ? "العميل" : "Customer"}</small><strong>{selected.customer?.name ?? selected.fulfillment.contactName ?? "—"}</strong></span>
              <span><small>{ar ? "الإجمالي" : "Total"}</small><strong>{money(selected.order.totalMinor, selected.order.currency, language)}</strong></span>
              <span><small>{ar ? "المتبقي" : "Balance"}</small><strong>{money(selected.order.balanceDueMinor, selected.order.currency, language)}</strong></span>
            </div>

            <div className="cashier-closed-orders__lines">
              {selected.cart.lines.length ? selected.cart.lines.map((line) => (
                <div key={line.id}>
                  <span>{ar ? line.productNameAr : line.productNameEn}</span>
                  <strong>× {line.quantityMilli / 1000}</strong>
                  <bdi>{money(line.totalMinor, selected.order.currency, language)}</bdi>
                </div>
              )) : <small>{ar ? "لا توجد أصناف في المعاملة." : "No items in this transaction."}</small>}
            </div>

            {completedRefundCount > 0 ? (
              <Alert tone="info" title={ar ? `استرداد (${completedRefundCount})` : `Refund (${completedRefundCount})`}>
                {ar ? "تتضمن هذه المعاملة استرداداً منفذاً." : "This transaction includes a completed refund."}
              </Alert>
            ) : null}

            {refundApprovalSubmitted ? (
              <Alert
                tone="info"
                title={ar ? "تم إرسال طلب الاسترداد للمدير" : "Refund approval request submitted"}
                onDismiss={() => setRefundApprovalSubmitted(false)}
              >
                <Stack>
                  <span>{ar ? "لم يتم تنفيذ الاسترداد بعد. افتح موافقات المدير لمراجعة الطلب واعتماده وتنفيذه." : "The refund has not executed yet. Open Manager Approvals to review, approve and execute the request."}</span>
                  {onOpenManagerApprovals ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid="open-manager-approvals-from-refund"
                      onClick={onOpenManagerApprovals}
                    >
                      {ar ? "فتح موافقات المدير" : "Open Manager Approvals"}
                    </Button>
                  ) : null}
                </Stack>
              </Alert>
            ) : null}

            {refundMode ? (
              <Stack>
                {replacementRequired ? <GiftCardReplacementFields language={language} permitted={canSetGiftCardExpiry}
                  noExpiry={noExpiry} expiry={expiry} onNoExpiry={setNoExpiry} onExpiry={setExpiry}
                  disabled={refundBusy || Boolean(pendingRefund)} /> : null}
                {pendingRefund ? <Alert tone="info" title={ar ? "أعد المحاولة لاستعادة نتيجة الاسترداد نفسه" : "Retry to recover the same refund result"} /> : null}
                <TextAreaField
                  data-testid="refund-reason"
                  label={ar ? "سبب الاسترداد" : "Refund reason"}
                  value={refundReason}
                  disabled={refundBusy || Boolean(pendingRefund)}
                  onChange={(event) => setRefundReason(event.target.value)}
                />
                {refundableLines.map((line) => {
                  const selectedQuantity = refundQuantities[line.id] ?? 0;
                  const checked = selectedQuantity > 0;
                  return (
                    <div className="refund-line" key={line.id}>
                      <Checkbox
                        data-testid={`refund-line-select-${line.id}`}
                        label={line.name}
                        checked={checked}
                        disabled={refundBusy || Boolean(pendingRefund)}
                        onChange={(event) => setRefundQuantities((current) => ({
                          ...current,
                          [line.id]: event.target.checked ? line.availableQuantityMilli : 0
                        }))}
                      />
                      <Field
                        data-testid={`refund-quantity-${line.id}`}
                        label={ar ? "كمية الاسترداد" : "Refund quantity"}
                        type="number"
                        min={0.001}
                        max={line.availableQuantityMilli / 1000}
                        step={0.001}
                        disabled={!checked || refundBusy || Boolean(pendingRefund)}
                        value={checked ? selectedQuantity / 1000 : ""}
                        onChange={(event) => setRefundQuantities((current) => ({
                          ...current,
                          [line.id]: Math.round(Number(event.target.value) * 1000)
                        }))}
                      />
                    </div>
                  );
                })}
                <RefundAllocationPreview {...allocationPreview} retry={allocationPreview.refresh} language={language} />
                <Inline>
                  <Button variant="secondary" size="sm" disabled={refundBusy || Boolean(pendingRefund)} onClick={resetRefundForm}>
                    {ar ? "إلغاء" : "Cancel"}
                  </Button>
                  <Button
                    data-testid="process-refund"
                    size="sm"
                    disabled={refundBusy || (replacementRequired && !canSetGiftCardExpiry) || (!pendingRefund && (!allocationPreview.preview || !refundReason.trim() || !Object.values(refundQuantities).some((value) => value > 0)))}
                    onClick={() => void processRefund()}
                  >
                    {refundBusy ? (ar ? "جارٍ التنفيذ…" : "Processing…") : pendingRefund ? (ar ? "استعادة نتيجة الاسترداد" : "Recover refund result") : replacementRequired ? (ar ? "إصدار بطاقة بديلة وإتمام الاسترداد" : "Issue replacement and complete refund") : (ar ? "تنفيذ الاسترداد" : "Process refund")}
                  </Button>
                </Inline>
              </Stack>
            ) : null}

            {selectedRefunds.filter(refund => refund.status === "Completed").map(refund =>
              <RefundDocumentPanel key={`document-${refund.id}`} orderId={selected.order.id} refundId={refund.id} refundNumber={refund.refundNumber} language={language} />)}

            {selectedRefunds.filter(refund => refund.status === "Completed" && refund.paymentAllocations.some(allocation => allocation.method === "GiftCard"))
              .map(refund => <RefundGiftCardPrintPanel key={refund.id} orderId={selected.order.id} refundId={refund.id} language={language} />)}

            <Inline>
              {selected.receipt ? (
                <>
                  <Button
                    data-testid="print-closed-payment-receipt"
                    size="sm"
                    onClick={() => onPrintReceipt(selected.receipt!, "payment")}
                  >
                    {ar ? "طباعة إيصال الدفع" : "Print payment receipt"}
                  </Button>
                  {canPrintFiscal(selected.receipt) ? (
                    <Button
                      data-testid="print-closed-tax-invoice"
                      size="sm"
                      variant="secondary"
                      onClick={() => onPrintReceipt(selected.receipt!, "fiscal")}
                    >
                      {ar ? "طباعة الفاتورة الضريبية" : "Print tax invoice"}
                    </Button>
                  ) : null}
                </>
              ) : null}
              {onCorrectCustomer && selected.receipt && selected.order.paymentStatus !== "Unpaid" ? (
                <Button
                  data-testid="correct-closed-order-customer"
                  size="sm"
                  variant="secondary"
                  onClick={() => { viewGeneration.current++; onCorrectCustomer(selected); }}
                >
                  {ar ? "بيانات العميل / فاتورة ضريبية" : "Customer details / tax invoice"}
                </Button>
              ) : null}
              {!refundMode && refundableLines.length > 0 && selected.order.paymentStatus !== "Unpaid" ? (
                <Button
                  data-testid="open-closed-order-refund"
                  size="sm"
                  variant="secondary"
                  onClick={() => setRefundMode(true)}
                >
                  {ar ? "استرداد" : "Refund"}
                </Button>
              ) : null}
            </Inline>

            {!selected.receipt ? (
              <Alert tone="info" title={ar ? "لا يوجد إيصال صادر" : "No issued receipt"}>
                {ar ? "لا تتوفر طباعة إيصال دفع لهذه المعاملة." : "This transaction has no issued payment receipt to reprint."}
              </Alert>
            ) : null}

            <Button size="sm" variant="secondary" onClick={() => {
              viewGeneration.current++;
              setSelected(null);
              setSelectedRefunds([]);
              setSelectedLoading(false);
              setError("");
              setRefundApprovalSubmitted(false);
              resetRefundForm();
            }}>
              {ar ? "العودة للقائمة" : "Back to list"}
            </Button>
          </section>
        ) : loading ? (
          <Spinner label={ar ? "جارٍ تحميل الطلبات المغلقة" : "Loading closed orders"} />
        ) : sortedTransactions.length ? (
          <div className="cashier-closed-orders__list">
            {sortedTransactions.map((transaction) => (
              <button
                type="button"
                className="cashier-closed-order"
                key={transaction.orderId}
                data-testid={`closed-order-${transaction.orderId}`}
                onClick={() => void openTransaction(transaction)}
              >
                <span className="cashier-closed-order__head">
                  <strong>#{transaction.orderNumber}</strong>
                  <span>
                    <StatusBadge tone={orderTone(transaction.orderStatus)}>{statusLabel(transaction.orderStatus, language)}</StatusBadge>
                    <StatusBadge tone={paymentTone(transaction.paymentStatus)}>{paymentLabel(transaction.paymentStatus, language)}</StatusBadge>
                  </span>
                </span>
                <span className="cashier-closed-order__identity">
                  <strong>{transaction.customerName ?? (ar ? "بدون عميل" : "No customer")}</strong>
                  <small>{serviceLabel(transaction.serviceType, language)} · {dateTime(transaction.completedAt ?? transaction.createdAt, language)}</small>
                </span>
                <span className="cashier-closed-order__totals">
                  <bdi>{money(transaction.totalMinor, transaction.currency, language)}</bdi>
                  {transaction.refundedMinor > 0 ? (
                    <small>{ar ? "مسترد" : "Refunded"}: {money(transaction.refundedMinor, transaction.currency, language)}</small>
                  ) : null}
                  {transaction.receiptNumber ? <small>{transaction.receiptNumber}</small> : null}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="activity"
            title={ar ? "لا توجد طلبات مغلقة" : "No closed orders"}
            description={ar ? "جرّب تغيير عبارة البحث." : "Try a different search term."}
          />
        )}
      </div>
    </Drawer>
  );
}
